import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {UnsubscribeOnDestroyAdapter} from '@shared';
import {DefaultResponse} from "@core/models/Http/DefaultResponse";
import {environment} from "@enviroment/environment";
import {Doctor} from "@core/models/Doctor";
import {HttpMesageHandlerService} from "@core/service/http-mesage-handler";
import {MsgResponse} from "@core/models/MsgResponse";
import {DoctorDetails} from "@core/models/DoctorDetails";
import {MedicByIdUser} from "@core/models/MedicByIdUser";
import {GeneralFunctionsService} from "@core/service/generalFunctions.service";

@Injectable({
  providedIn: 'root',
})

export class DoctorService extends UnsubscribeOnDestroyAdapter {
  isTblLoading = true;
  dataChange: BehaviorSubject<Doctor[]> = new BehaviorSubject<Doctor[]>([]);

  constructor(
    private httpClient: HttpClient,
    private httpMsgHandler: HttpMesageHandlerService,
    private gfs: GeneralFunctionsService
  ) {
    super();
  }

  get data(): Doctor[] {
    return this.dataChange.value;
  }

  /** CRUD METHODS */
  getAllDoctors(idGEMP: string): void {
    this.isTblLoading = true;

    const params = new HttpParams().set('idGEMP', (idGEMP ?? '').trim());

    this.subs.sink = this.httpClient
      .get<DefaultResponse<Doctor[]>>(
        `${environment.backend_url}Medicos/ByGEMP`,
        { params }
      )
      .subscribe({
        next: (data) => {
          this.isTblLoading = false;

          const combined = (data.data ?? []).map((value: Doctor) => ({
            ...value,
            nombres: `${value.nombres} ${value.primerApellido} ${value.segundoApellido}`.trim()
          }));

          this.dataChange.next(combined);
        },
        error: (error: HttpErrorResponse) => {
          this.isTblLoading = false;
          this.httpMsgHandler.handleMsg(error);
        },
      });
  }

  getAllDoctorsByIdBranch(idBranch: string): void {
    this.isTblLoading = true;

    const params = new HttpParams().set('idSucursal', (idBranch ?? '').trim());

    this.subs.sink = this.httpClient
      .get<DefaultResponse<Doctor[]>>(
        `${environment.backend_url}Medicos/BySucursal`,
        { params }
      )
      .subscribe({
        next: (data) => {
          this.isTblLoading = false;

          const combined = (data.data ?? []).map((value: Doctor) => ({
            ...value,
            nombres: `${value.nombres} ${value.primerApellido} ${value.segundoApellido}`.trim()
          }));

          this.dataChange.next(combined);
        },
        error: (error: HttpErrorResponse) => {
          this.isTblLoading = false;
          this.httpMsgHandler.handleMsg(error);
        },
      });
  }

  getDoctorByIdUser(idUser: string): Observable<DefaultResponse<DoctorDetails>> {
    const params = new HttpParams().set('idUsuario', (idUser ?? '').trim());

    return this.httpClient.get<DefaultResponse<DoctorDetails>>(
      `${environment.backend_url}Medicos/ByIdUsuario`,
      { params }
    );
  }

  getDoctorByIdDoctor(idDoctor: string): Observable<DefaultResponse<MedicByIdUser>> {
    const params = new HttpParams().set('idMedico', (idDoctor ?? '').trim());

    return this.httpClient.get<DefaultResponse<MedicByIdUser>>(
      `${environment.backend_url}Medicos/ByIdMedico`,
      { params }
    );
  }

  createUpdateDoctor(data: Doctor) {
    const cleanData = this.gfs.mayusAcentos(data);
    const url = `${environment.backend_url}Medicos`;

    return this.httpClient.post<DefaultResponse<string>>(url, cleanData);
  }

  upsertPiePagina(data: { detalle: string }) {
    return this.httpClient.post<DefaultResponse<string>>(
      `${environment.backend_url}PiePagina/upsert`,
      data
    );
  }

  getFormatosReceta(): Observable<DefaultResponse<any[]>> {
    return this.httpClient.get<DefaultResponse<any[]>>(
      `${environment.backend_url}Medicos/FormatosReceta`
    );
  }

  getFormatoRecetaByMedico(idMedico: string): Observable<DefaultResponse<any>> {
    const params = new HttpParams().set('idMedico', (idMedico ?? '').trim());

    return this.httpClient.get<DefaultResponse<any>>(
      `${environment.backend_url}Medicos/FormatoReceta`,
      { params }
    );
  }

  getFormatosHip(): Observable<DefaultResponse<any[]>> {
    return this.httpClient.get<DefaultResponse<any[]>>(
      `${environment.backend_url}Medicos/FormatosHip`
    );
  }

  getFormatoHipByMedico(idMedico: string): Observable<DefaultResponse<any>> {
    const params = new HttpParams().set('idMedico', (idMedico ?? '').trim());

    return this.httpClient.get<DefaultResponse<any>>(
      `${environment.backend_url}Medicos/FormatoHip`,
      { params }
    );
  }

  updateFormatoReceta(data: { idMedico: string; idFormatoReceta: number }): Observable<DefaultResponse<any>> {
    return this.httpClient.put<DefaultResponse<any>>(
      `${environment.backend_url}Medicos/FormatoReceta`,
      data
    );
  }

  updateFormatoHip(data: { idMedico: string; idFormatoHip: number }): Observable<DefaultResponse<any>> {
    return this.httpClient.put<DefaultResponse<any>>(
      `${environment.backend_url}Medicos/FormatoHip`,
      data
    );
  }

  getPiePagina(idPiePagina?: number | null, idMedico?: string | null): Observable<DefaultResponse<any>> {
    let params = new HttpParams();

    if (idPiePagina !== null && idPiePagina !== undefined) {
      params = params.set('idPiePagina', String(idPiePagina));
    }

    if (idMedico) {
      params = params.set('idMedico', String(idMedico).trim());
    }

    return this.httpClient.get<DefaultResponse<any>>(
      `${environment.backend_url}PiePagina`,
      { params }
    );
  }

  deletePiePagina(id: number | string): Observable<DefaultResponse<string>> {
    return this.httpClient.delete<DefaultResponse<string>>(
      `${environment.backend_url}PiePagina/${id}`
    );
  }

  deleteMedic(id: string): void {
    this.httpClient.delete(`${environment.backend_url}Medicos/${id}`)
      .subscribe({
        next: (res) => {
          this.httpMsgHandler.handleMsg(res as MsgResponse);
        },
        error: (error: HttpErrorResponse) => {
          this.httpMsgHandler.handleMsg(error);
        },
      });
  }
}
